import React, { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { History } from 'lucide-react';
import api from '../../services/api';
import { useI18n } from '../../i18n/I18nProvider';
import { useSession } from '../../session/SessionProvider';
import { PageHeader, DataTable, Badge, Avatar, EmptyState, Select, Button } from '../../ui';
import { auditActionKey, shortAuditPath } from '../../lib/auditActions';

const PAGE_SIZE = 50;
const METHOD_TONE = { POST: 'success', PUT: 'brand', PATCH: 'brand', DELETE: 'danger' };

export default function AuditPage() {
  return <AuditPanel />;
}

/** Журнал действий; tenantId — только действия этой компании (карточка компании у владельца) */
export function AuditPanel({ tenantId = null, embedded = false }) {
  const { t, fmt } = useI18n();
  const { isOwner, isViewingAsOwner } = useSession();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [method, setMethod] = useState('');
  const [loading, setLoading] = useState(true);

  // Колонка компании нужна только в консоли владельца (в кабинете компании все действия — её)
  const showCompany = isOwner && !isViewingAsOwner && !tenantId;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/audit', {
        params: { page, limit: PAGE_SIZE, ...(method ? { method } : {}), ...(tenantId ? { tenant_id: tenantId } : {}) },
      });
      setItems(res.data.items || []);
      setTotal(res.data.total || 0);
    } catch (err) {
      toast.error(err.response?.data?.error || t('common.error'));
    } finally {
      setLoading(false);
    }
  }, [page, method, tenantId, t]);

  useEffect(() => { load(); }, [load]);

  const resultBadge = (status) => {
    if (status >= 200 && status < 300) return <Badge tone="success">{t('audit.ok')}</Badge>;
    if (status === 401 || status === 403) return <Badge tone="warning">{t('audit.denied')}</Badge>;
    return <Badge tone="danger">{t('audit.failed')} {status}</Badge>;
  };

  const columns = [
    {
      key: 'created_at',
      header: t('audit.when'),
      render: (a) => (
        <span className="tabular text-zinc-500" title={fmt.dateTime(a.created_at)}>{fmt.relative(a.created_at)}</span>
      ),
    },
    {
      key: 'admin_username',
      header: t('audit.who'),
      render: (a) => (
        <div className="flex items-center gap-2.5">
          <Avatar name={a.admin_username} size="sm" />
          <div>
            <p className="font-medium text-zinc-900 dark:text-zinc-100">{a.admin_username}</p>
            <p className="text-xs text-zinc-500">{t(`roles.${a.admin_role}`)}</p>
          </div>
        </div>
      ),
    },
    {
      key: 'path',
      header: t('audit.what'),
      render: (a) => {
        const key = auditActionKey(a.method, a.path);
        return (
          <div className="min-w-0">
            <p className="flex items-center gap-2 text-zinc-900 dark:text-zinc-100">
              {key ? t(key) : <Badge tone={METHOD_TONE[a.method] ?? 'neutral'}>{t(`audit.methods.${a.method}`)}</Badge>}
            </p>
            <code className="block truncate font-mono text-[11px] text-zinc-400">{shortAuditPath(a.path)}</code>
          </div>
        );
      },
    },
    ...(showCompany ? [{
      key: 'tenant_name',
      header: t('audit.where'),
      hideOnMobile: true,
      render: (a) => a.tenant_name ?? <span className="text-zinc-400">{t('audit.platform')}</span>,
    }] : []),
    {
      key: 'fields',
      header: t('audit.fields'),
      hideOnMobile: true,
      render: (a) => {
        const fields = a.details?.fields ?? [];
        if (!fields.length) return <span className="text-zinc-400">—</span>;
        const values = a.details?.values ?? {};
        return (
          <span className="block max-w-xs truncate text-xs text-zinc-500" title={fields.map((f) => (values[f] ? `${f}: ${values[f]}` : f)).join(', ')}>
            {fields.map((f) => (values[f] ? `${f}: ${values[f]}` : f)).join(', ')}
          </span>
        );
      },
    },
    { key: 'status', header: t('audit.result'), render: (a) => resultBadge(a.status) },
  ];

  const filters = (
    <>
      <Select value={method} onChange={(e) => { setMethod(e.target.value); setPage(1); }} className="w-40" aria-label={t('audit.what')}>
        <option value="">{t('common.all')}</option>
        {['POST', 'PUT', 'PATCH', 'DELETE'].map((m) => <option key={m} value={m}>{t(`audit.methods.${m}`)}</option>)}
      </Select>
      <Button variant="secondary" onClick={load}>{t('common.refresh')}</Button>
    </>
  );

  return (
    <>
      {embedded
        ? <div className="mb-4 flex justify-end gap-2">{filters}</div>
        : <PageHeader title={t('audit.title')} description={t('audit.subtitle')} actions={filters} />}
      <DataTable
        columns={columns}
        rows={items}
        loading={loading}
        empty={<EmptyState icon={History} title={t('audit.empty')} text={t('audit.emptyHint')} />}
        pagination={{ page, pages: Math.max(1, Math.ceil(total / PAGE_SIZE)), onPage: setPage }}
      />
    </>
  );
}
